/**
 * Account Dashboard Component
 *
 * Main account page with navigation.
 */

import { useState } from 'react';
import { useAuth, useShop } from '@/context';
import { Button } from '../shared/Button';
import { OrderHistory } from './OrderHistory';
import { AddressBook } from './AddressBook';
import { WishlistPage } from './WishlistPage';
import { AccountInfo } from './AccountInfo';
import styles from './AccountDashboard.module.css';

type Tab = 'orders' | 'addresses' | 'wishlist' | 'account';

export function AccountDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('orders');
  const { user, logout, isLoading } = useAuth();
  const { t } = useShop();

  if (!user) {
    return null;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'orders', label: t('myOrders') },
    { key: 'addresses', label: t('myAddresses') },
    { key: 'wishlist', label: t('myWishlist') },
    { key: 'account', label: t('myAccount') },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Error handled in context
    }
  };

  return (
    <div className={styles.container}>
      <aside className={styles.sidebar}>
        <div className={styles.userInfo}>
          <div className={styles.userName}>
            <span className={styles.greeting}>Bonjour,</span>
            <span className={styles.name}>{user.first_name || user.display_name || user.email}</span>
          </div>
        </div>

        <nav className={styles.nav}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.navItem} ${
                activeTab === tab.key ? styles.active : ''
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              <TabIcon tab={tab.key} />
              {tab.label}
            </button>
          ))}
        </nav>

        <Button
          variant="ghost"
          size="small"
          onClick={handleLogout}
          isLoading={isLoading}
          className={styles.logoutButton}
        >
          {t('logout')}
        </Button>
      </aside>

      <main className={styles.main}>
        {activeTab === 'orders' && <OrderHistory />}
        {activeTab === 'addresses' && <AddressBook />}
        {activeTab === 'wishlist' && <WishlistPage />}
        {activeTab === 'account' && <AccountInfo />}
      </main>
    </div>
  );
}

function TabIcon({ tab }: { tab: Tab }) {
  switch (tab) {
    case 'orders':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'addresses':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case 'wishlist':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case 'account':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
  }
}
